import { useEffect } from 'react';
import { Discount as DiscountIcon } from '@mafc/icon';
import { Box, Flex, LinearProgress } from '@mafc/trolly';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Card from 'common/Card';
import { CardText, CardTitle } from 'common/Card/Card.style';
import LinkActionButton from 'common/LinkActionButton';
import useDrawerControls from 'hooks/useDrawerControls';
import { AppliedVoucher } from 'network/response/ICart';
import { ICartState } from 'pages/Cart/redux/reducer';
import { ICheckoutState } from 'pages/Checkout/redux/reducer';
import AvailablePromotionsDrawer from '../AvailablePromotionsDrawer';
import PromoCodeDrawer from '../PromoCodeDrawer';
import { getDiscountAmount } from '../Promo/Promo.helper';
import { fetchAvailablePromotions } from './redux/AvailablePromotions.action';
import { appliedCouponApiSuccess as appliedCouponApiSuccessAction } from 'pages/Checkout/components/AvailablePromotions/redux/AvailablePromotions.action';
import { resetPromo } from 'pages/Checkout/redux/actions/promo.action';
import { createMarkup } from 'utils/html.util';
import { IPrice, getFormattedPrice } from 'utils/price.util';
import { IOrderSummaryState } from 'common/OrderSummary/redux/orderSummary.reducer';
import TrackerCheckout from 'utils/Tracker.checkout';

import * as S from './AvailablePromotion.style';
import { ILoyaltyPointState } from '../LoyaltyPoints/redux/LoyaltyPoints.reducer';

const AvailablePromotions = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    isOpen: isAvailablePromoDrawerOpen,
    openDrawer: openAvailablePromoDrawer,
    closeDrawer: closeAvailablePromoDrawer,
  } = useDrawerControls();

  const {
    isOpen: isPromoDrawerOpen,
    openDrawer: openPromoDrawer,
    closeDrawer: closePromoDrawer,
  } = useDrawerControls();

  const closePromoCodeDrawer = () => {
    closePromoDrawer();
    dispatch(appliedCouponApiSuccessAction(false));
  };

  const {
    appliedPromo,
    loading,
    appliedCouponApiSuccess,
    totalPriceWithTax,
    isToggleSwitchedOn,
  }: {
    appliedPromo: AppliedVoucher | null;
    loading: boolean;
    appliedCouponApiSuccess: boolean;
    totalPriceWithTax: IPrice;
    isToggleSwitchedOn: boolean;
  } = useSelector(
    (state: {
      orderSummary: IOrderSummaryState;
      cart: ICartState;
      checkout: ICheckoutState;
      loyalty: ILoyaltyPointState;
    }) => {
      return {
        appliedPromo: state.cart.cartData.appliedVouchers
          ? state.cart.cartData.appliedVouchers[0]
          : null,
        loading: state.checkout.promo.loading,
        appliedCouponApiSuccess: state.checkout.availablePromotions.appliedCouponApiSuccess,
        totalPriceWithTax: state.orderSummary.totalPriceWithTax,
        isToggleSwitchedOn:
          state.loyalty && state.cart.cartData.totalPrice.value === 0
            ? state.loyalty.isToggleSwitchedOn
            : false,
      };
    }
  );

  useEffect(() => {
    if (appliedCouponApiSuccess) {
      closeAvailablePromoDrawer();
      openPromoDrawer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedCouponApiSuccess]);

  const availablePromotionOnClick = () => {
    if (isToggleSwitchedOn) {
      return;
    }
    TrackerCheckout.selectAVoucherClicked();
    dispatch(fetchAvailablePromotions(appliedPromo ? appliedPromo.code : ''));
    dispatch(resetPromo());
    openAvailablePromoDrawer();
  };

  const enterCodeBtnClickHandler = () => {
    TrackerCheckout.availablePromotionsEnterCode();
    closeAvailablePromoDrawer();
    openPromoDrawer();
  };

  return (
    <>
      <S.AvailablePromotions>
        <Card
          testId="available_promo_code_card"
          variant="scattered"
          onClick={availablePromotionOnClick}
        >
          <Flex>
            <DiscountIcon />

            <Box ml={3}>
              <CardTitle isApplied={!!appliedPromo}>
                {appliedPromo
                  ? t('promo.promoCodeApplied', {
                      promoCode: appliedPromo.voucherCode,
                    })
                  : t('promo.selectVoucher')}
              </CardTitle>

              <CardText>
                {appliedPromo ? (
                  <>
                    {t('promo.savings')} <S.Savings>{getDiscountAmount(appliedPromo)}</S.Savings>
                  </>
                ) : (
                  t('promo.fromAvailableOffers')
                )}
              </CardText>
            </Box>
          </Flex>

          <LinkActionButton
            label={appliedPromo ? t('promo.edit') : ''}
            dataTestId="promo-action-btn"
            disabled={isToggleSwitchedOn}
          />
          {loading && <LinearProgress position="bottom" />}
        </Card>

        {!!appliedPromo && totalPriceWithTax.value > 0 && (
          <S.DueAmount
            dangerouslySetInnerHTML={createMarkup(
              t('promo.dueAmount', {
                amount: getFormattedPrice(totalPriceWithTax, false, true, true),
              })
            )}
          />
        )}
      </S.AvailablePromotions>

      {isAvailablePromoDrawerOpen && (
        <AvailablePromotionsDrawer
          isOpen={isAvailablePromoDrawerOpen}
          closeDrawer={closeAvailablePromoDrawer}
          enterCodeBtnClickHandler={enterCodeBtnClickHandler}
        />
      )}

      {isPromoDrawerOpen && (
        <PromoCodeDrawer isOpen={isPromoDrawerOpen} closeDrawer={closePromoCodeDrawer} />
      )}
    </>
  );
};

export default AvailablePromotions;
